/**
 * @file Detail-view checklist section rendering and DOM wiring.
 */

import { openConfirmationModal } from '../ui/confirmationModal.js';
import { createSelectControl } from '../ui/controls/selectControl.js';
import {
    bindNativeFieldBehavior,
    INLINE_EDIT_INPUT_CLASSES,
} from '../forms/nativeFieldStyles.js';
import {
    findItem,
} from "../utils/itemLookup.js";
import {
    getMatchingChecklistTemplates,
} from "./checklistTemplates.js";
import {
    sanitizeHTML,
} from "../utils/contentUtils.js";

const CHECKLIST_ROW_CLASSES = [
    'checklist-item-row',
    'group',
    'relative',
    'flex',
    'min-h-10',
    'items-start',
    'py-1',
    'sm:min-h-7',
    'sm:py-0',
].join(' ');
const CHECKLIST_DRAG_HANDLE_CLASSES = [
    'checklist-drag-handle',
    'hidden',
    'h-4',
    'w-4',
    'mt-1.5',
    'mr-1.5',
    'appearance-none',
    'flex-shrink-0',
    'cursor-grab',
    'items-center',
    'justify-center',
    'border-0',
    '!bg-transparent',
    'overflow-hidden',
    'p-0',
    'text-gray-400',
    'transition-colors',
    'hover:!bg-transparent',
    'hover:text-[var(--dfc-brand-primary)]',
    'focus:outline-none',
    'focus:!bg-transparent',
    'focus:text-[var(--dfc-brand-primary)]',
    'active:cursor-grabbing',
    'sm:inline-flex',
].join(' ');
const CHECKLIST_TEXT_CLASSES = [
    'checklist-item-text-display',
    'box-border',
    'min-h-9',
    'inline-block',
    'max-w-full',
    'cursor-text',
    'break-words',
    'pr-6',
    'border-b',
    'border-transparent',
    'py-1',
    'text-base',
    'leading-7',
    'transition-colors',
    'hover:border-transparent',
    'hover:shadow-[inset_0_-1px_0_var(--dfc-brand-primary)]',
    'focus:border-transparent',
    'focus:shadow-[inset_0_-2px_0_rgba(71,85,105,0.55)]',
    'focus:outline-none',
    'sm:min-h-7',
    'sm:text-sm',
    'sm:leading-5',
].join(' ');
const CHECKLIST_TEXT_INPUT_CLASSES = [
    'checklist-item-text-input',
    'hidden',
    INLINE_EDIT_INPUT_CLASSES,
    'block',
    'box-border',
    'min-h-9',
    'resize-none',
    'overflow-hidden',
    'font-normal',
    'text-base',
    'leading-7',
    '!py-1',
    'sm:min-h-7',
    'sm:text-sm',
    'sm:leading-5',
].join(' ');
const VISIBLE_CHECKLIST_TEXT_INPUT_CLASSES =
    CHECKLIST_TEXT_INPUT_CLASSES.replace('hidden', '').replace(/\s+/g, ' ').trim();
const CHECKLIST_GHOST_INPUT_CLASSES = [
    INLINE_EDIT_INPUT_CLASSES,
    'box-border',
    'min-h-10',
    'text-base',
    'leading-7',
    '!py-1',
    'sm:min-h-7',
    'sm:text-sm',
    'sm:leading-5',
].join(' ');
const CHECKLIST_MARKER_CLASSES = [
    'inline-flex',
    'h-6',
    'w-6',
    'flex-shrink-0',
    'items-center',
    'justify-center',
    'rounded',
    'border',
    'border-gray-300',
    'bg-white',
    'overflow-hidden',
    'text-transparent',
    'transition-colors',
    'peer-checked:border-gray-400',
    'peer-checked:bg-gray-400',
    'peer-checked:text-white',
    'peer-focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]',
    'hover:border-[var(--dfc-brand-primary)]',
    'sm:h-4',
    'sm:w-4',
].join(' ');
const CHECKLIST_ACTION_BUTTON_CLASSES = [
    'inline-flex',
    'h-8',
    'w-7',
    'appearance-none',
    'items-center',
    'justify-center',
    'border-0',
    '!bg-transparent',
    'overflow-hidden',
    'p-0',
    'text-gray-400',
    'transition-colors',
    'hover:!bg-transparent',
    'hover:text-gray-900',
    'focus:outline-none',
    'focus:!bg-transparent',
    'focus:text-gray-900',
    'disabled:pointer-events-none',
    'disabled:opacity-35',
    'dark:hover:text-gray-100',
    'dark:focus:text-gray-100',
    'sm:mt-1.5',
    'sm:h-4',
    'sm:w-4',
].join(' ');
const CHECKLIST_DELETE_BUTTON_CLASSES = [
    'checklist-item-delete-btn',
    'inline-flex',
    'h-8',
    'w-7',
    'appearance-none',
    'items-center',
    'justify-center',
    'border-0',
    '!bg-transparent',
    'overflow-hidden',
    'p-0',
    'text-gray-400',
    'transition-colors',
    'hover:!bg-transparent',
    'hover:text-red-600',
    'focus:outline-none',
    'focus:!bg-transparent',
    'focus:text-red-600',
    'sm:mt-1.5',
    'sm:h-4',
    'sm:w-4',
].join(' ');
const CHECKLIST_MANAGEMENT_TOGGLE_CLASSES = [
    'checklist-management-toggle',
    'inline-flex',
    'h-10',
    'w-10',
    'appearance-none',
    'items-center',
    'justify-center',
    'border-0',
    'bg-transparent',
    'p-0',
    'text-gray-400',
    'transition-colors',
    'hover:text-[var(--dfc-brand-primary)]',
    'focus:outline-none',
    'focus:text-[var(--dfc-brand-primary)]',
    'dark:text-gray-400',
].join(' ');
const CHECKLIST_ACTION_ICON_CLASSES = [
    'material-symbols-outlined',
    'block',
    'pointer-events-none',
    'text-[22px]',
    'leading-none',
    'sm:text-[16px]',
].join(' ');
const CHECKLIST_SUMMARY_BUTTON_CLASSES = [
    'completed-checklist-summary',
    'mt-2',
    'flex',
    'min-h-8',
    'w-full',
    'items-center',
    'gap-1.5',
    'text-left',
    'text-sm',
    'transition-colors',
    'focus:outline-none',
    'focus:text-[var(--dfc-brand-primary)]',
].join(' ');
const CHECKLIST_MOVE_FLASH_COLOR = 'rgba(243, 244, 246, 0.7)';
const CHECKLIST_MOVE_FLASH_DARK_COLOR = 'rgba(75, 85, 99, 0.2)';
const CHECKLIST_MOVE_FLASH_DURATION_MS = 1100;
const CHECKLIST_MOVE_FLASH_FADE_IN_MS = 120;
const CHECKLIST_MOVE_FLASH_HOLD_MS = 300;
const CHECKLIST_MOVE_FLASH_FADE_OUT_MS = 800;
const CHECKLIST_MOVE_FLASH_MAX_OPACITY = 0.85;
const CHECKLIST_UI_SESSION = {
    itemId: null,
    managementMode: false,
    completedExpanded: false,
    moveFlash: null,
};

/**
 * Clears detail-checklist-only UI state when the detail session ends.
 */
export function resetChecklistUiSession() {
    CHECKLIST_UI_SESSION.itemId = null;
    CHECKLIST_UI_SESSION.managementMode = false;
    CHECKLIST_UI_SESSION.completedExpanded = false;
    CHECKLIST_UI_SESSION.moveFlash = null;
}

function getChecklistUiSession(itemId) {
    if (CHECKLIST_UI_SESSION.itemId !== itemId) {
        CHECKLIST_UI_SESSION.itemId = itemId;
        CHECKLIST_UI_SESSION.managementMode = false;
        CHECKLIST_UI_SESSION.completedExpanded = false;
        CHECKLIST_UI_SESSION.moveFlash = null;
    }
    return CHECKLIST_UI_SESSION;
}

function getChecklistMoveFlashOpacity(elapsedMs) {
    if (elapsedMs < CHECKLIST_MOVE_FLASH_FADE_IN_MS) {
        return CHECKLIST_MOVE_FLASH_MAX_OPACITY * (elapsedMs / CHECKLIST_MOVE_FLASH_FADE_IN_MS);
    }
    if (elapsedMs < CHECKLIST_MOVE_FLASH_HOLD_MS) {
        return CHECKLIST_MOVE_FLASH_MAX_OPACITY;
    }
    const fadeElapsed = elapsedMs - CHECKLIST_MOVE_FLASH_HOLD_MS;
    const fadeProgress = Math.min(fadeElapsed / CHECKLIST_MOVE_FLASH_FADE_OUT_MS, 1);
    return CHECKLIST_MOVE_FLASH_MAX_OPACITY * (1 - fadeProgress);
}

function renderChecklistMoveFlashLayer(row, flashState) {
    if (!row) {return;}
    const elapsedMs = Date.now() - flashState.startedAt;
    if (elapsedMs >= CHECKLIST_MOVE_FLASH_DURATION_MS) {return;}
    const flashContainer = row.closest('.checklist-section-container');
    if (!flashContainer) {return;}
    flashContainer.querySelector('.checklist-move-flash-layer')?.remove();
    const rowBox = row.getBoundingClientRect();
    const containerBox = flashContainer.getBoundingClientRect();
    const topOffset = Math.max(
        0,
        Math.round(rowBox.top - containerBox.top + flashContainer.scrollTop),
    );
    const flashColor = row.closest('.dark') ?
        CHECKLIST_MOVE_FLASH_DARK_COLOR :
        CHECKLIST_MOVE_FLASH_COLOR;
    const layer = document.createElement('div');
    layer.className = 'checklist-move-flash-layer';
    row.style.zIndex = '1';
    const currentOpacity = getChecklistMoveFlashOpacity(elapsedMs);
    Object.assign(layer.style, {
        position: 'absolute',
        top: `${topOffset}px`,
        height: `${Math.round(rowBox.height)}px`,
        left: '0',
        right: '0',
        borderRadius: '0.375rem',
        backgroundColor: flashColor,
        opacity: String(currentOpacity),
        pointerEvents: 'none',
        zIndex: '0',
    });
    flashContainer.prepend(layer);

    const startFadeOut = () => {
        const currentElapsedMs = Date.now() - flashState.startedAt;
        const remainingFadeMs = Math.max(
            CHECKLIST_MOVE_FLASH_DURATION_MS - currentElapsedMs,
            0,
        );
        layer.style.transition = `opacity ${remainingFadeMs}ms ease-out`;
        layer.style.opacity = '0';
        window.setTimeout(() => {
            layer.remove();
        }, remainingFadeMs + 50);
    };

    if (elapsedMs < CHECKLIST_MOVE_FLASH_FADE_IN_MS) {
        const fadeInRemainingMs = CHECKLIST_MOVE_FLASH_FADE_IN_MS - elapsedMs;
        layer.style.transition = `opacity ${fadeInRemainingMs}ms ease-out`;
        window.requestAnimationFrame(() => {
            layer.style.opacity = String(CHECKLIST_MOVE_FLASH_MAX_OPACITY);
        });
    }

    const holdRemainingMs = CHECKLIST_MOVE_FLASH_HOLD_MS - elapsedMs;
    if (holdRemainingMs > 0) {
        window.setTimeout(startFadeOut, holdRemainingMs);
    } else {
        window.requestAnimationFrame(startFadeOut);
    }
}

function startChecklistMoveFlash(row, uiSession) {
    uiSession.moveFlash = {
        itemId: row.dataset.id,
        startedAt: Date.now(),
    };
    renderChecklistMoveFlashLayer(row, uiSession.moveFlash);
    window.setTimeout(() => {
        if (uiSession.moveFlash?.itemId === row.dataset.id) {
            uiSession.moveFlash = null;
        }
    }, CHECKLIST_MOVE_FLASH_DURATION_MS + 100);
}

function resumeChecklistMoveFlash(checklistSection, uiSession) {
    const flashState = uiSession.moveFlash;
    if (!flashState?.itemId) {return;}
    if (Date.now() - flashState.startedAt >= CHECKLIST_MOVE_FLASH_DURATION_MS) {
        uiSession.moveFlash = null;
        return;
    }
    const row = Array.from(
        checklistSection.querySelectorAll('.checklist-item-row'),
    ).find((candidate) => candidate.dataset.id === flashState.itemId);
    renderChecklistMoveFlashLayer(row, flashState);
}

function refreshChecklistArrowButtons(list) {
    if (!list) {return;}
    const rows = Array.from(list.querySelectorAll('.checklist-item-row'));
    rows.forEach((row, index) => {
        const upButton = row.querySelector('.reorder-up-btn');
        const downButton = row.querySelector('.reorder-down-btn');
        if (upButton) {upButton.disabled = index === 0;}
        if (downButton) {downButton.disabled = index === rows.length - 1;}
    });
}

function getChecklistTextStateClasses(isCompleted) {
    return isCompleted ?
        'line-through text-gray-400' :
        'text-gray-700 dark:text-gray-200';
}

function getChecklistTextClasses(isManagementMode) {
    if (!isManagementMode) {return CHECKLIST_TEXT_CLASSES;}
    return [
        CHECKLIST_TEXT_CLASSES
            .replace('cursor-text', 'cursor-default')
            .replace('hover:shadow-[inset_0_-1px_0_var(--dfc-brand-primary)]', '')
            .replace('focus:shadow-[inset_0_-2px_0_rgba(71,85,105,0.55)]', ''),
        'hover:shadow-none',
        'focus:shadow-none',
    ].join(' ');
}

function renderChecklistCheckboxHtml(isCompleted) {
    return `
            <label class="mr-2 mt-1.5 inline-flex flex-shrink-0 cursor-pointer items-center">
                <input type="checkbox" class="checklist-item-checkbox peer sr-only" ${isCompleted ? 'checked' : ''}>
                <span class="${CHECKLIST_MARKER_CLASSES}" aria-hidden="true">
                    <span class="material-symbols-outlined pointer-events-none text-[18px] leading-none sm:text-[13px]">check</span>
                </span>
            </label>
    `;
}

function renderChecklistActionsHtml(
    index = -1,
    itemCount = 0,
    { isCompleted = false, isManagementMode = false } = {},
) {
    const isFirst = index === 0;
    const isLast = itemCount > 0 && index === itemCount - 1;
    if (isManagementMode && isCompleted) {return '';}
    const shouldShowReorder = !isManagementMode || !isCompleted;
    const visibilityClasses = isManagementMode ?
        'flex' :
        'hidden opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 sm:flex';
    return `
            <div class="checklist-row-actions ${visibilityClasses} ml-auto h-10 flex-shrink-0 flex-row items-center gap-0.5 pl-2 sm:ml-0 sm:h-7 sm:items-start sm:gap-1 sm:pl-1">
                ${shouldShowReorder ? `
                <button type="button" class="reorder-up-btn ${CHECKLIST_ACTION_BUTTON_CLASSES}" title="Move Up" aria-label="Move item up" ${isFirst ? 'disabled' : ''}>
                    <span class="${CHECKLIST_ACTION_ICON_CLASSES}">expand_less</span>
                </button>
                <button type="button" class="reorder-down-btn ${CHECKLIST_ACTION_BUTTON_CLASSES}" title="Move Down" aria-label="Move item down" ${isLast ? 'disabled' : ''}>
                    <span class="${CHECKLIST_ACTION_ICON_CLASSES}">expand_more</span>
                </button>
                ` : ''}
                <button type="button" class="${CHECKLIST_DELETE_BUTTON_CLASSES}" title="Delete Item" aria-label="Delete checklist item">
                    <span class="${CHECKLIST_ACTION_ICON_CLASSES}">close</span>
                </button>
            </div>
    `;
}

function renderChecklistDragHandleHtml(isDraggable) {
    if (!isDraggable) {return '';}
    return `
            <button type="button" class="${CHECKLIST_DRAG_HANDLE_CLASSES}" draggable="true" title="Drag to reorder" aria-label="Drag checklist item to reorder">
                <span class="${CHECKLIST_ACTION_ICON_CLASSES}">drag_indicator</span>
            </button>
    `;
}

function renderChecklistItemsHtml(itemsArray, options = {}) {
    const itemCount = itemsArray.length;
    const {
        section = 'active',
        isManagementMode = false,
    } = options;
    const isActiveSection = section === 'active';
    return itemsArray.map((cItem, index) => `
        <div class="${CHECKLIST_ROW_CLASSES}" data-id="${cItem.id}" data-order="${Number(cItem.order ?? index)}" data-section="${section}">
            ${renderChecklistDragHandleHtml(isManagementMode && isActiveSection)}
            ${renderChecklistCheckboxHtml(cItem.isCompleted)}
            <div class="checklist-row-body flex min-w-0 flex-1 items-start">
                <div class="checklist-text-shell relative min-w-0 max-w-full flex-grow sm:flex-grow-0">
                <div class="${getChecklistTextClasses(isManagementMode)} ${getChecklistTextStateClasses(cItem.isCompleted)}" title="${isManagementMode ? '' : 'Click to edit'}" ${isManagementMode ? '' : 'tabindex="0"'}>${sanitizeHTML(cItem.text || '')}</div>
                <textarea class="${CHECKLIST_TEXT_INPUT_CLASSES} ${getChecklistTextStateClasses(cItem.isCompleted)}" rows="1" data-auto-grow="true" placeholder="Empty item...">${sanitizeHTML(cItem.text || '')}</textarea>
                </div>
                ${renderChecklistActionsHtml(index, itemCount, {
                    isCompleted: Boolean(cItem.isCompleted),
                    isManagementMode,
                })}
            </div>
        </div>
        `).join('');
}

/**
 * Renders the Checklist section within the details view.
 * @param {object} item Current item data.
 * @returns {string} Checklist section HTML.
 */
export function renderChecklistSection(item) {
    const uiSession = getChecklistUiSession(item.id);
    const isManagementMode = uiSession.managementMode;
    const completedExpanded = uiSession.completedExpanded;
    const checklistMap = item.checklist || {};
    const checklistItems = Object.entries(checklistMap).map(
        ([id, data]) => ({ id, ...data }),
    );

    const incompleteItems = checklistItems
        .filter(i => !i.isCompleted)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    const completedItems = checklistItems
        .filter(i => i.isCompleted)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const incompleteHtml = incompleteItems.length > 0
        ? renderChecklistItemsHtml(incompleteItems, {
            section: 'active',
            isManagementMode,
        })
        : '';
    const completedHtml = completedItems.length > 0 && completedExpanded
        ? renderChecklistItemsHtml(completedItems, {
            section: 'completed',
            isManagementMode,
        })
        : '';
    const managementButtonClasses = isManagementMode
        ? `${CHECKLIST_MANAGEMENT_TOGGLE_CLASSES} !text-[var(--dfc-brand-accent)]`
        : CHECKLIST_MANAGEMENT_TOGGLE_CLASSES;
    const completedSummaryClasses = completedExpanded
        ? `${CHECKLIST_SUMMARY_BUTTON_CLASSES} text-[var(--dfc-brand-accent)]`
        : `${CHECKLIST_SUMMARY_BUTTON_CLASSES} text-gray-500 hover:text-[var(--dfc-brand-primary)] dark:text-gray-400`;

    return `
        <div class="checklist-section-container relative bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 mt-6" data-checklist-management="${isManagementMode ? 'true' : 'false'}">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-semibold dark:text-gray-100">Checklist</h2>
                <div class="flex items-center space-x-1">
                    <button type="button" class="${managementButtonClasses}" title="Manage Checklist" aria-label="Manage checklist" aria-pressed="${isManagementMode ? 'true' : 'false'}">
                        <span class="material-symbols-outlined pointer-events-none text-[20px] leading-none">edit</span>
                    </button>
                    <button type="button" class="apply-checklist-template-btn quick-add-task-btn" title="Apply Template" aria-label="Apply checklist template">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                </div>
            </div>

            <div class="checklist-items-container relative">
                <div class="active-checklist-items mb-1">
                    ${incompleteHtml}
                </div>

                <div class="flex items-center group mt-1">
                    <div class="mr-2 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                        <span class="material-symbols-outlined text-[18px] leading-none text-[var(--dfc-brand-accent)]">add</span>
                    </div>
                    <div class="flex-grow w-full relative">
                        <input type="text" class="checklist-ghost-input ${CHECKLIST_GHOST_INPUT_CLASSES}" placeholder="Add a new item">
                    </div>
                </div>

                ${completedItems.length > 0 ? `
                <button type="button" class="${completedSummaryClasses}" aria-expanded="${completedExpanded ? 'true' : 'false'}">
                    <span class="material-symbols-outlined text-[18px] leading-none">${completedExpanded ? 'expand_less' : 'expand_more'}</span>
                    <span>${completedItems.length} checked items</span>
                </button>
                ` : ''}
                ${completedHtml ? `
                <div class="completed-checklist-items opacity-70">
                        ${completedHtml}
                </div>
                ` : ''}
            </div>
        </div>
    `;
}

function updateChecklistMemory(item, data) {
    const currentItem = findItem(item.id) || item;
    currentItem.checklist = data;
    item.checklist = data;
}

function createChecklistRow(text) {
    const newRow = document.createElement('div');
    newRow.className = CHECKLIST_ROW_CLASSES;
    newRow.dataset.id = crypto.randomUUID();
    newRow.dataset.order = '';
    newRow.dataset.section = 'active';
    newRow.innerHTML = `
                ${renderChecklistCheckboxHtml(false)}
                <div class="checklist-row-body flex min-w-0 flex-1 items-start">
                    <div class="checklist-text-shell relative min-w-0 max-w-full flex-grow">
                    <div class="${CHECKLIST_TEXT_CLASSES} hidden text-gray-700 dark:text-gray-200" title="Click to edit" tabindex="0">${sanitizeHTML(text)}</div>
                    <textarea class="${VISIBLE_CHECKLIST_TEXT_INPUT_CLASSES} text-gray-700 dark:text-gray-200" rows="1" data-auto-grow="true" placeholder="Empty item...">${sanitizeHTML(text)}</textarea>
                    </div>
                    ${renderChecklistActionsHtml()}
                </div>
    `;
    return newRow;
}

function enterChecklistEditMode(row) {
    const display = row?.querySelector('.checklist-item-text-display');
    const input = row?.querySelector('.checklist-item-text-input');
    const shell = row?.querySelector('.checklist-text-shell');
    if (!display || !input || !shell) {return null;}

    shell.classList.add('flex-grow');
    shell.classList.remove('sm:flex-grow-0');
    display.classList.add('hidden');
    input.classList.remove('hidden');
    bindNativeFieldBehavior(row);
    input.focus();
    input.selectionStart = input.selectionEnd = input.value.length;
    return input;
}

function exitChecklistEditMode(row) {
    const display = row?.querySelector('.checklist-item-text-display');
    const input = row?.querySelector('.checklist-item-text-input');
    const shell = row?.querySelector('.checklist-text-shell');
    if (!display || !input || !shell) {return;}

    display.textContent = input.value;
    display.classList.remove('hidden');
    input.classList.add('hidden');
    shell.classList.add('flex-grow', 'sm:flex-grow-0');
}

/**
 * Attaches listeners for the Checklist UI.
 * @param {HTMLElement} container Detail view container.
 * @param {object} item Current item.
 * @param {string} itemType Current item type.
 * @param {object} callbacks Listener callbacks.
 * @param {Function} callbacks.saveChecklistState Existing checklist save helper.
 * @param {Function} callbacks.toggleChecklistItem Existing checklist toggle helper.
 * @param {Function} callbacks.renderDetailsView Detail re-render callback.
 */
export function attachChecklistListeners(
    container,
    item,
    itemType,
    { saveChecklistState, toggleChecklistItem, renderDetailsView },
) {
    const checklistSection = container.querySelector('.checklist-section-container');
    if (!checklistSection) {return;}
    bindNativeFieldBehavior(checklistSection);
    const uiSession = getChecklistUiSession(item.id);

    const itemsContainer = checklistSection.querySelector('.checklist-items-container');
    if (!itemsContainer) {return;}

    const ghostInput = checklistSection.querySelector('.checklist-ghost-input');
    const deletedChecklistIds = new Set();

    const gatherChecklistData = () => {
        const rows = itemsContainer.querySelectorAll('.checklist-item-row');
        const newData = {};
        const presentIds = new Set();
        let orderIndex = 0;
        rows.forEach((row) => {
            const id = row.dataset.id;
            const text = row.querySelector('.checklist-item-text-input').value.trim();
            const isCompleted = row.querySelector('.checklist-item-checkbox').checked;
            if (text) {
                row.dataset.order = String(orderIndex);
                presentIds.add(id);
                newData[id] = { text, isCompleted, order: orderIndex++ };
            }
        });
        Object.entries(item.checklist || {})
            .filter(([id]) => !presentIds.has(id) && !deletedChecklistIds.has(id))
            .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
            .forEach(([id, data]) => {
                const text = String(data?.text || '').trim();
                if (!text) {return;}
                newData[id] = {
                    text,
                    isCompleted: Boolean(data.isCompleted),
                    order: orderIndex++,
                };
            });
        return newData;
    };

    const saveSilently = async () => {
        const newData = gatherChecklistData();
        updateChecklistMemory(item, newData);
        await saveChecklistState(item.id, itemType, newData);
        deletedChecklistIds.clear();
    };

    let draggedChecklistRow = null;

    const getDragAfterElement = (list, y) => {
        const draggableRows = Array.from(
            list.querySelectorAll('.checklist-item-row:not(.checklist-dragging)'),
        );
        return draggableRows.reduce((closest, row) => {
            const box = row.getBoundingClientRect();
            const offset = y - box.top - (box.height / 2);
            if (offset < 0 && offset > closest.offset) {
                return { offset, element: row };
            }
            return closest;
        }, { offset: Number.NEGATIVE_INFINITY, element: null }).element;
    };

    const activeList = itemsContainer.querySelector('.active-checklist-items');

    if (activeList) {
        activeList.addEventListener('dragstart', (e) => {
            const row = e.target.closest('.checklist-item-row');
            const handle = e.target.closest('.checklist-drag-handle');
            if (!uiSession.managementMode || !row || !handle ||
                row.dataset.section !== 'active') {
                e.preventDefault();
                return;
            }
            draggedChecklistRow = row;
            row.classList.add('checklist-dragging', 'opacity-50');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', row.dataset.id || '');
        });

        activeList.addEventListener('dragover', (e) => {
            if (!draggedChecklistRow) {return;}
            e.preventDefault();
            const afterElement = getDragAfterElement(activeList, e.clientY);
            if (!afterElement) {
                activeList.appendChild(draggedChecklistRow);
            } else {
                activeList.insertBefore(draggedChecklistRow, afterElement);
            }
        });

        activeList.addEventListener('drop', async (e) => {
            if (!draggedChecklistRow) {return;}
            e.preventDefault();
            await saveSilently();
            renderDetailsView();
        });

        activeList.addEventListener('dragend', () => {
            if (!draggedChecklistRow) {return;}
            draggedChecklistRow.classList.remove('checklist-dragging', 'opacity-50');
            draggedChecklistRow = null;
        });
    }

    const managementToggle = checklistSection.querySelector('.checklist-management-toggle');
    if (managementToggle) {
        managementToggle.addEventListener('click', () => {
            uiSession.managementMode = !uiSession.managementMode;
            renderDetailsView();
        });
    }

    const completedSummary = checklistSection.querySelector('.completed-checklist-summary');
    if (completedSummary) {
        completedSummary.addEventListener('click', () => {
            uiSession.completedExpanded = !uiSession.completedExpanded;
            renderDetailsView();
        });
    }

    resumeChecklistMoveFlash(checklistSection, uiSession);

    itemsContainer.addEventListener('click', (e) => {
        if (!e.target.closest('.checklist-item-text-display')) {return;}
        if (uiSession.managementMode) {return;}
        const row = e.target.closest('.checklist-item-row');
        enterChecklistEditMode(row);
    });

    itemsContainer.addEventListener('keydown', (e) => {
        if (!e.target.matches('.checklist-item-text-display')) {return;}
        if (uiSession.managementMode) {return;}
        if (e.key !== 'Enter' && e.key !== ' ') {return;}
        e.preventDefault();
        e.target.click();
    });

    itemsContainer.addEventListener('change', async (e) => {
        if (!e.target.matches('.checklist-item-checkbox')) {return;}
        const row = e.target.closest('.checklist-item-row');
        const id = row.dataset.id;
        const isCompleted = e.target.checked;
        const display = row.querySelector('.checklist-item-text-display');
        const input = row.querySelector('.checklist-item-text-input');

        if (isCompleted) {
            display.classList.add('line-through', 'text-gray-400');
            display.classList.remove('text-gray-700', 'dark:text-gray-200');
            input.classList.add('line-through', 'text-gray-400');
            input.classList.remove('text-gray-700', 'dark:text-gray-200');
        } else {
            display.classList.remove('line-through', 'text-gray-400');
            display.classList.add('text-gray-700', 'dark:text-gray-200');
            input.classList.remove('line-through', 'text-gray-400');
            input.classList.add('text-gray-700', 'dark:text-gray-200');
        }

        if (item.checklist && item.checklist[id]) {
            item.checklist[id].isCompleted = isCompleted;
        }

        toggleChecklistItem(item.id, itemType, id, isCompleted);

        if (!document.activeElement?.matches('.checklist-item-text-input, .checklist-ghost-input')) {
            renderDetailsView();
        }
    });

    itemsContainer.addEventListener('focusout', (e) => {
        if (!e.target.matches('.checklist-item-text-input')) {return;}
        const input = e.target;
        setTimeout(() => {
            if (document.activeElement === input) {return;}
            const row = input.closest('.checklist-item-row');
            if (!row) {return;}
            if (input.value.trim() === '') {
                deletedChecklistIds.add(row.dataset.id);
                row.remove();
                saveSilently();
                return;
            }
            exitChecklistEditMode(row);
            saveSilently();
        }, 100);
    });

    itemsContainer.addEventListener('keydown', (e) => {
        if (!e.target.matches('.checklist-item-text-input')) {return;}
        const input = e.target;
        const row = input.closest('.checklist-item-row');
        if (e.key === 'Enter') {
            e.preventDefault();
            input.blur();

            setTimeout(() => {
                const nextRow = row.nextElementSibling;
                if (nextRow && nextRow.matches('.checklist-item-row')) {
                    const nextDisplay = nextRow.querySelector('.checklist-item-text-display');
                    if (nextDisplay) {
                        nextDisplay.click();
                    }
                } else {
                    ghostInput.focus();
                }
            }, 110);
        } else if (e.key === 'Backspace' && input.value === '') {
            e.preventDefault();
            const prev = row.previousElementSibling;
            deletedChecklistIds.add(row.dataset.id);
            row.remove();
            saveSilently();
            if (prev && prev.matches('.checklist-item-row')) {
                enterChecklistEditMode(prev);
            } else {
                ghostInput.focus();
            }
        }
    });

    ghostInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') {return;}
        e.preventDefault();
        if (ghostInput.value.trim() === '') {return;}

        const text = ghostInput.value.trim();
        ghostInput.value = '';
        const newRow = createChecklistRow(text);

        const emptyMsg = itemsContainer.querySelector('#empty-checklist-msg');
        if (emptyMsg) {emptyMsg.remove();}

        if (activeList) {activeList.appendChild(newRow);}
        bindNativeFieldBehavior(newRow);

        ghostInput.focus();
        saveSilently();
    });

    itemsContainer.addEventListener('click', (e) => {
        const deleteBtn = e.target.closest('.checklist-item-delete-btn');
        if (!deleteBtn) {return;}
        const row = deleteBtn.closest('.checklist-item-row');
        deletedChecklistIds.add(row.dataset.id);
        row.remove();
        saveSilently();
    });

    itemsContainer.addEventListener('click', (e) => {
        const upBtn = e.target.closest('.reorder-up-btn');
        const downBtn = e.target.closest('.reorder-down-btn');
        if (!upBtn && !downBtn) {return;}

        const row = e.target.closest('.checklist-item-row');
        if (upBtn && row.previousElementSibling &&
            row.previousElementSibling.matches('.checklist-item-row')) {
            row.parentNode.insertBefore(row, row.previousElementSibling);
            refreshChecklistArrowButtons(row.parentElement);
            startChecklistMoveFlash(row, uiSession);
            saveSilently();
        } else if (downBtn && row.nextElementSibling &&
            row.nextElementSibling.matches('.checklist-item-row')) {
            row.parentNode.insertBefore(row.nextElementSibling, row);
            refreshChecklistArrowButtons(row.parentElement);
            startChecklistMoveFlash(row, uiSession);
            saveSilently();
        }
    });

    const templateBtn = checklistSection.querySelector('.apply-checklist-template-btn');
    if (templateBtn) {
        templateBtn.addEventListener('click', () => {
            const templates = getMatchingChecklistTemplates(itemType, item.types || [], true);

            if (templates.length === 0) {
                openConfirmationModal(
                    'No Templates',
                    'No matching templates found for this item.',
                    () => { },
                    false,
                    'OK',
                );
                return;
            }

            let messageHtml;

            if (templates.length > 1) {
                const defaultTemplateId = templates[0]?.id || '';
                messageHtml = `
        <div class="mb-3 text-sm text-gray-600 dark:text-gray-300">Multiple checklist templates match this item. Select one to apply:</div>
            <input type="hidden" id="manual-checklist-template-select" value="${sanitizeHTML(defaultTemplateId)}">
            <div id="manual-checklist-template-control"></div>
    `;
            } else {
                messageHtml = `Apply "${sanitizeHTML(templates[0].name)}" template? This will append to your current list.`;
            }

            openConfirmationModal('Apply Template', messageHtml, async () => {
                let selectedId = templates[0].id;
                if (templates.length > 1) {
                    const selectEl = document.getElementById('manual-checklist-template-select');
                    if (selectEl) {selectedId = selectEl.value;}
                }

                const selectedTemplate = templates.find(t => t.id === selectedId);
                if (!selectedTemplate || !selectedTemplate.items) {return;}

                const newData = gatherChecklistData();
                const itemsArray = Object.values(selectedTemplate.items)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                let orderIndex = Object.keys(newData).length;
                itemsArray.forEach(i => {
                    newData[crypto.randomUUID()] = {
                        text: i.text,
                        isCompleted: false,
                        order: orderIndex++,
                    };
                });

                updateChecklistMemory(item, newData);
                await saveChecklistState(item.id, itemType, newData);
                renderDetailsView();
            }, false, 'Apply', null, 'Applying...', "Don't Apply");

            if (templates.length > 1) {
                const templateControlRoot = document.getElementById(
                    'manual-checklist-template-control',
                );
                const templateInput = document.getElementById(
                    'manual-checklist-template-select',
                );
                if (templateControlRoot && templateInput) {
                    createSelectControl({
                        root: templateControlRoot,
                        options: templates.map((template) => ({
                            value: template.id,
                            label: template.name || template.id,
                        })),
                        value: templateInput.value || templates[0]?.id || '',
                        searchable: templates.length > 6,
                        mirrorInput: templateInput,
                        accessibleName: 'Checklist template',
                    });
                }
            }
        });
    }
}
